/**
 * As ações de uma linha da aba Documentos (E205.F4.T02/T03, ADR-0147 §2.5): **Abrir** (PDF e imagem,
 * no painel ao lado), **Confirmar leitura** (abre o cartão na linha), **Rever leitura** (o painel do
 * dado do campo) e **Baixar** (o original; a recusa diz a causa).
 *
 * E207.F6.T03 (owner, 01/11) — a regra das ações numa linha, a mesma nas outras abas da mesa: a ordem
 * é Abrir · Confirmar leitura (ou Revisar leitura / Exclusão da análise) · Rever leitura · Baixar; a
 * única sólida (`primary`) é a que pede o perito agora (Confirmar leitura); abrir, revisar e rever
 * levam a moldura (`secondary`); Baixar e Recolher vão sem borda (`ghost`). Em linha, `size="sm"`,
 * numa linha só, à direita; a recusa do download fica embaixo das ações.
 *
 * E216.F4 (GUS-317 item 3): **Excluir** (sem borda, por último) abre o diálogo do motivo e do
 * fundamento; o arquivo já excluído não tem a ação.
 */
import type { MouseEvent } from 'react';

import { useAbrirDado } from '@/components/DocumentoDaMesa/pedidoDoDado';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { usePainelDoDocumento } from '@/lib/painelDoDocumento';

import type { LinhaDoDocumento } from './linhasDosDocumentos';
import { useBaixarOriginal } from './useBaixarOriginal';

const t = ptBR.documentosDoCaso;
const tm = ptBR.mesaUnica.documento;
const tr = ptBR.recorteDoDocumento;
const tx = ptBR.exclusaoNaMesa;

/** "Abrir o dado lido" (E209.F3.T06; antes "Rever leitura", ao lado de "Revisar leitura"): o painel do
 * dado do campo que pede revisão (só com o documento editável). */
function Rever({ linha }: { linha: LinhaDoDocumento }): JSX.Element | null {
  const abrirDado = useAbrirDado();
  const alvo = linha.rever;
  if (alvo === null || abrirDado === null) return null;
  const rever = (e: MouseEvent<HTMLButtonElement>): void =>
    abrirDado({
      origem: {
        tipo: 'lido',
        chave: alvo.campo,
        linha: null,
        revisao: null,
        referente: alvo.referente,
      },
      impresso: '',
      secao: null,
      elemento: e.currentTarget,
    });
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      aria-label={t.acoes.reverO.replace('{nome}', linha.nome)}
      onClick={rever}
      data-testid="linha-rever"
    >
      {t.acoes.rever}
    </Button>
  );
}

/** "Recortar" (E214.F1, GUS-314): o recorte manual do documento que abre no painel. */
function Recortar({
  linha,
  onRecortar,
}: {
  linha: LinhaDoDocumento;
  onRecortar: (() => void) | undefined;
}): JSX.Element | null {
  if (linha.abre !== 'painel' || onRecortar === undefined) return null;
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      aria-label={tr.botaoDe.replace('{nome}', linha.nome)}
      onClick={onRecortar}
      data-testid="linha-recortar"
    >
      {tr.botao}
    </Button>
  );
}

/** "Excluir" (E216.F4, GUS-317 item 3): o arquivo vivo sai da análise com motivo e fundamento. */
function Excluir({
  linha,
  onExcluir,
}: {
  linha: LinhaDoDocumento;
  onExcluir: (() => void) | undefined;
}): JSX.Element | null {
  if (linha.abre === 'excluido' || onExcluir === undefined) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-label={tx.botaoDe.replace('{nome}', linha.nome)}
      onClick={onExcluir}
      data-testid="linha-excluir"
    >
      {tx.botao}
    </Button>
  );
}

export function AcoesDaLinha({
  caseId,
  linha,
  aberta,
  onConfirmar,
  onRecortar,
  onExcluir,
}: {
  caseId: string;
  linha: LinhaDoDocumento;
  aberta: boolean;
  onConfirmar: () => void;
  /** E214.F1 (GUS-314): abre o recorte manual deste documento; sem ele, o botão não aparece. */
  onRecortar?: (() => void) | undefined;
  /** E216.F4 (GUS-317 item 3): abre a exclusão deste documento; sem ela, o botão não aparece. */
  onExcluir?: (() => void) | undefined;
}): JSX.Element {
  const painel = usePainelDoDocumento();
  // "Baixar": o original pela rota da custódia; a recusa diz a causa embaixo das ações.
  const baixar = useBaixarOriginal(caseId, linha.id, linha.nome);
  return (
    <div className="flex flex-col items-end gap-sc-1">
      <div className="flex items-center justify-end gap-sc-1">
        {linha.abre === 'painel' && painel !== null ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label={t.acoes.abrirO.replace('{nome}', linha.nome)}
            onClick={() => painel.abrir({ documentoId: linha.id, pagina: 1 })}
            data-testid="linha-abrir"
          >
            {t.acoes.abrir}
          </Button>
        ) : null}
        <Recortar linha={linha} onRecortar={onRecortar} />
        {linha.acaoDoCartao !== null ? (
          <Button
            type="button"
            variant={
              aberta ? 'ghost' : linha.acaoDoCartao === 'confirmar' ? 'primary' : 'secondary'
            }
            size="sm"
            aria-expanded={aberta}
            onClick={onConfirmar}
            data-testid="linha-confirmar"
            data-acao={linha.acaoDoCartao}
          >
            {aberta ? t.acoes.recolherConfirmacao : t.acoes.cartao[linha.acaoDoCartao]}
          </Button>
        ) : null}
        <Rever linha={linha} />
        {linha.abre !== 'excluido' ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={t.acoes.baixarO.replace('{nome}', linha.nome)}
            disabled={baixar.baixando}
            onClick={baixar.baixar}
            data-testid="linha-baixar"
          >
            {baixar.baixando ? t.acoes.baixando : t.acoes.baixar}
          </Button>
        ) : null}
        <Excluir linha={linha} onExcluir={onExcluir} />
      </div>
      {baixar.motivo !== null ? (
        <p
          role="alert"
          className="max-w-[32ch] whitespace-normal text-right text-caption text-danger-700"
          data-testid="linha-baixar-erro"
          data-motivo={baixar.motivo}
        >
          {tm.porQue[baixar.motivo]}
        </p>
      ) : null}
    </div>
  );
}
